import { DragEvent, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './auth';
import {
  getPlaylist,
  renamePlaylist,
  deletePlaylist,
  setPlaylistVisibility,
  addVideoToPlaylist,
  removeVideoFromPlaylist,
  reorderPlaylist,
  PlaylistError,
} from './playlists';
import { editSharedPlaylist, ApiError } from './api';
import { getMyUpload, listMyUploads } from './uploads';
import { getSharedVideo, listSharedLibrary } from './shared-library';
import { config } from './config';
import { EditableTitle, CopyRow } from './ui';
import { ItemCard } from './item-card';
import { AddNewTile } from './add-new-tile';
import { ShareIconAction, ConfirmIconAction, ReorderIconActions } from './quick-actions';
import { SearchablePicker, PickerOption } from './searchable-picker';
import {
  ListToolbar,
  SortOrder,
  ViewMode,
  sortItems,
  filterByTitle,
  useViewMode,
  useSelection,
  SelectionBar,
} from './list-controls';
import { mapWithConcurrency } from './concurrency';
import { Playlist } from './types';

function playlistErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof PlaylistError) return e.message;
  if (e instanceof ApiError) {
    if (e.message === 'not_shared') return "This playlist isn't shared anymore.";
    if (e.message === 'video_not_shared') {
      return 'This playlist is shared — only shared videos can be added to it.';
    }
  }
  return fallback;
}

interface PlaylistVideoInfo {
  videoId: string;
  title: string;
  href: string;
  thumbnailUrl?: string;
}

export function PlaylistDetailPage() {
  const { playlistId } = useParams<{ playlistId: string }>();
  const { identityId } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortOrder>('default');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useViewMode('playlist');
  const [addingVideo, setAddingVideo] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkSummary, setBulkSummary] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);
  const selection = useSelection<PlaylistVideoInfo>((v) => v.videoId);

  const query = useQuery({
    queryKey: ['playlist', identityId, playlistId],
    enabled: !!identityId && !!playlistId,
    queryFn: () => getPlaylist(identityId!, playlistId!),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['playlist', identityId, playlistId] });

  const visibilityMutation = useMutation({
    mutationFn: (next: 'private' | 'shared') => setPlaylistVisibility(query.data!, next),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(playlistErrorMessage(e, "Couldn't update visibility.")),
  });

  // Any signed-in user may rename, or add/remove a video on, a SHARED playlist (most shared
  // playlists — e.g. the Wistia bulk import — have no real owner, so the direct-client functions
  // below, which are IAM-scoped to the exact owner via leading-keys, can never work for anyone
  // else). Private playlists keep using those same direct-client functions, unchanged — they only
  // ever work for the real owner anyway, which is the correct behavior for something private.
  const isShared = query.data?.visibility === 'shared';

  const removeMutation = useMutation({
    mutationFn: async (videoId: string) => {
      if (isShared) {
        await editSharedPlaylist(query.data!.ownerId, query.data!.playlistId, { removeVideoId: videoId });
      } else {
        await removeVideoFromPlaylist(query.data!, videoId);
      }
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deletePlaylist(query.data!.ownerId, query.data!.playlistId),
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderedVideoIds: string[]) => {
      if (isShared) {
        await editSharedPlaylist(query.data!.ownerId, query.data!.playlistId, { reorderVideoIds: orderedVideoIds });
      } else {
        await reorderPlaylist(query.data!, orderedVideoIds);
      }
    },
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(playlistErrorMessage(e, "Couldn't reorder that.")),
  });

  const videoIds = query.data?.videoIds ?? [];
  const ownerId = query.data?.ownerId;

  // Resolves every videoId's title/href/thumbnail against SharedLibrary first (always readable),
  // then the owner's own UserUploads if the viewer IS the owner — same lookup PlaylistVideoRow
  // used to do per-row, now done once up front so the results can be sorted/filtered as a whole.
  // A videoId that resolves to neither (a private video in someone else's playlist) simply never
  // appears, per the "shared playlists only show shared videos" rule: IAM's leading-keys condition
  // already makes that entry unreadable to a non-owner regardless.
  const videoQueries = useQueries({
    queries: videoIds.map((videoId) => ({
      queryKey: ['playlist-video', videoId, ownerId],
      enabled: !!ownerId,
      queryFn: async (): Promise<PlaylistVideoInfo | null> => {
        const shared = await getSharedVideo(videoId);
        if (shared) {
          return { videoId, title: shared.title, href: `/shared/${videoId}`, thumbnailUrl: shared.thumbnailUrl };
        }
        if (identityId === ownerId) {
          const mine = await getMyUpload(ownerId!, videoId);
          if (mine) {
            return { videoId, title: mine.filename, href: `/library/${videoId}`, thumbnailUrl: mine.thumbnailUrl };
          }
        }
        return null;
      },
    })),
  });
  const resolvedVideos = videoQueries
    .map((q) => q.data)
    .filter((v): v is PlaylistVideoInfo => !!v);
  // Recomputed plainly each render — see the equivalent comment in library-page.tsx.
  const visibleVideos = sortItems(filterByTitle(resolvedVideos, search, (v) => v.title), sort, (v) => v.title);

  if (query.isLoading) return <p className="muted">Loading…</p>;
  if (!query.data) {
    return (
      <div>
        <p className="error">This playlist isn't available.</p>
        <Link to="/playlists">Back to playlists</Link>
      </div>
    );
  }

  const playlist = query.data;
  const isOwner = playlist.ownerId === identityId;
  // If you can see this playlist at all, you're allowed to edit its name/videos: either it's your
  // own (private or shared — leading-keys already gated visibility on it), or it's someone else's
  // SHARED one, which the new Lambda path opens up to any signed-in user.
  const canEdit = isOwner || isShared;
  // Reordering only makes sense against the playlist's own stored order (sort === 'default' —
  // see list-controls.tsx), and only against the full list — a search filter narrows to a subset,
  // which would leave the full order ambiguous. Also suspended while in selection mode, matching
  // ItemCard's own "selection mode swaps every other action for a checkbox" behavior.
  const canReorder = canEdit && sort === 'default' && search === '' && !selectionMode;

  async function handleBulkRemove() {
    const ids = [...selection.selectedIds];
    if (ids.length === 0) return;
    if (!confirm(`Remove ${ids.length} video${ids.length === 1 ? '' : 's'} from this playlist?`)) return;
    setBulkPending(true);
    setBulkSummary(null);
    const results = await mapWithConcurrency(
      ids,
      async (videoId) => {
        if (isShared) {
          await editSharedPlaylist(playlist.ownerId, playlist.playlistId, { removeVideoId: videoId });
        } else {
          await removeVideoFromPlaylist(playlist, videoId);
        }
      },
      8,
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').map((r) => r.item);
    const failed = results.filter((r) => r.status === 'rejected');
    if (succeeded.length > 0) succeeded.forEach((id) => selection.toggle(id));
    setBulkSummary(
      failed.length === 0
        ? `Removed ${succeeded.length} video${succeeded.length === 1 ? '' : 's'}.`
        : `Removed ${succeeded.length} of ${ids.length}. ${failed.length} couldn't be removed — try again.`,
    );
    setBulkPending(false);
    invalidate();
  }

  function moveVideo(fromIndex: number, toIndex: number) {
    if (toIndex < 0 || toIndex >= visibleVideos.length || fromIndex === toIndex) return;
    const next = [...visibleVideos];
    const [moved] = next.splice(fromIndex, 1);
    if (!moved) return;
    next.splice(toIndex, 0, moved);
    reorderMutation.mutate(next.map((v) => v.videoId));
  }

  const handleDragOver = (e: DragEvent) => e.preventDefault();
  const handleDrop = (index: number) => (e: DragEvent) => {
    e.preventDefault();
    if (dragIndex !== null) moveVideo(dragIndex, index);
    setDragIndex(null);
  };

  if (deleteMutation.isSuccess) {
    return (
      <div>
        <p>Playlist deleted.</p>
        <Link to="/playlists">Back to playlists</Link>
      </div>
    );
  }

  return (
    <div>
      <Link to="/playlists" className="muted">
        ← Back to playlists
      </Link>
      <EditableTitle
        title={playlist.name}
        onSave={async (next) => {
          if (isShared) {
            await editSharedPlaylist(playlist.ownerId, playlist.playlistId, { name: next });
          } else {
            await renamePlaylist(playlist.ownerId, playlist.playlistId, next);
          }
          invalidate();
        }}
      />
      {playlist.visibility === 'shared' && <span className="status-badge">Shared</span>}

      {isOwner && (
        <div className="card" style={{ marginTop: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <button
            className="secondary"
            disabled={visibilityMutation.isPending}
            onClick={() =>
              visibilityMutation.mutate(playlist.visibility === 'shared' ? 'private' : 'shared')
            }
          >
            {playlist.visibility === 'shared' ? 'Make private' : 'Make shared'}
          </button>
          <button
            className="secondary"
            style={{ marginLeft: 8 }}
            onClick={() => {
              if (confirm('Delete this playlist? This cannot be undone.')) deleteMutation.mutate();
            }}
          >
            Delete playlist
          </button>
          {error && <p className="error">{error}</p>}
        </div>
      )}

      {canEdit && (
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <h2>Play in order</h2>
          <p className="muted">
            ⚠️ Anyone with this link can play through the playlist in order, no sign-in required —
            whether the playlist is private or shared. Don't share it for sensitive content.
          </p>
          <CopyRow label="Feed link" value={`https://${config.spaDomain}/feed/${playlist.playlistId}`} />
        </div>
      )}

      {videoIds.length > 0 && (
        <ListToolbar
          sort={sort}
          onSortChange={setSort}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onSearch={setSearch}
          searchPlaceholder="Search this playlist…"
          selectionMode={canEdit ? selectionMode : undefined}
          onToggleSelectionMode={
            canEdit
              ? () => {
                  setSelectionMode((v) => !v);
                  selection.clear();
                  setBulkSummary(null);
                }
              : undefined
          }
        />
      )}

      {canEdit && selectionMode && (
        <SelectionBar count={selection.count} onSelectAll={() => selection.selectAll(visibleVideos)} onClear={selection.clear}>
          <button onClick={handleBulkRemove} disabled={selection.count === 0 || bulkPending}>
            Remove selected
          </button>
        </SelectionBar>
      )}
      {bulkSummary && <p className="muted">{bulkSummary}</p>}

      {videoIds.length === 0 && !canEdit ? (
        <p className="muted">No videos in this playlist yet.</p>
      ) : viewMode === 'grid' ? (
        <div className="item-grid">
          {canEdit && (
            <AddNewTile viewMode={viewMode} label="Add a video" onClick={() => setAddingVideo(true)} />
          )}
          {visibleVideos.map((video, index) => (
            <PlaylistVideoItem
              key={video.videoId}
              video={video}
              viewMode={viewMode}
              canEdit={canEdit}
              removing={removeMutation.isPending && removeMutation.variables === video.videoId}
              onRemove={() => removeMutation.mutate(video.videoId)}
              canReorder={canReorder}
              canMoveUp={index > 0}
              canMoveDown={index < visibleVideos.length - 1}
              onMoveUp={() => moveVideo(index, index - 1)}
              onMoveDown={() => moveVideo(index, index + 1)}
              onDragStart={() => setDragIndex(index)}
              onDragOver={handleDragOver}
              onDrop={handleDrop(index)}
              onDragEnd={() => setDragIndex(null)}
              selectionMode={canEdit && selectionMode}
              selected={selection.isSelected(video.videoId)}
              onToggleSelected={() => selection.toggle(video.videoId)}
            />
          ))}
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {canEdit && (
            <AddNewTile viewMode={viewMode} label="Add a video" onClick={() => setAddingVideo(true)} />
          )}
          {visibleVideos.map((video, index) => (
            <PlaylistVideoItem
              key={video.videoId}
              video={video}
              viewMode={viewMode}
              canEdit={canEdit}
              removing={removeMutation.isPending && removeMutation.variables === video.videoId}
              onRemove={() => removeMutation.mutate(video.videoId)}
              canReorder={canReorder}
              canMoveUp={index > 0}
              canMoveDown={index < visibleVideos.length - 1}
              onMoveUp={() => moveVideo(index, index - 1)}
              onMoveDown={() => moveVideo(index, index + 1)}
              onDragStart={() => setDragIndex(index)}
              onDragOver={handleDragOver}
              onDrop={handleDrop(index)}
              onDragEnd={() => setDragIndex(null)}
              selectionMode={canEdit && selectionMode}
              selected={selection.isSelected(video.videoId)}
              onToggleSelected={() => selection.toggle(video.videoId)}
            />
          ))}
        </ul>
      )}
      {videoIds.length > 0 && visibleVideos.length === 0 && (
        <p className="muted">No videos match your search.</p>
      )}

      {canEdit && addingVideo && (
        <AddVideoControl playlist={playlist} isShared={isShared} onAdded={invalidate} />
      )}
    </div>
  );
}

function PlaylistVideoItem({
  video,
  viewMode,
  canEdit,
  removing,
  onRemove,
  canReorder,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  selectionMode,
  selected,
  onToggleSelected,
}: {
  video: PlaylistVideoInfo;
  viewMode: ViewMode;
  canEdit: boolean;
  removing: boolean;
  onRemove: () => void;
  canReorder: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDragStart: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
  onDragEnd: () => void;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelected: () => void;
}) {
  const shareUrl = `https://${config.spaDomain}/watch/${video.videoId}`;
  return (
    <ItemCard
      href={video.href}
      thumbnailUrl={video.thumbnailUrl}
      title={video.title}
      viewMode={viewMode}
      selectionMode={selectionMode}
      checkbox={{ checked: selected, onChange: onToggleSelected }}
      draggable={canReorder}
      onDragStart={canReorder ? onDragStart : undefined}
      onDragOver={canReorder ? onDragOver : undefined}
      onDrop={canReorder ? onDrop : undefined}
      onDragEnd={canReorder ? onDragEnd : undefined}
      dragHandle={
        canReorder ? (
          <span className="icon-button" aria-hidden="true" style={{ cursor: 'grab' }}>
            ⠿
          </span>
        ) : undefined
      }
      actions={
        <>
          <ShareIconAction shareUrl={shareUrl} />
          {canReorder && (
            <ReorderIconActions
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              canMoveUp={canMoveUp}
              canMoveDown={canMoveDown}
            />
          )}
          {canEdit && (
            <ConfirmIconAction
              icon="➖"
              label="Remove from playlist"
              confirmMessage="Remove this video from the playlist?"
              onConfirm={onRemove}
              disabled={removing}
            />
          )}
        </>
      }
    />
  );
}

function AddVideoControl({
  playlist,
  isShared,
  onAdded,
}: {
  playlist: Playlist;
  isShared: boolean;
  onAdded: () => void;
}) {
  const { identityId } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const myUploads = useQuery({
    queryKey: ['my-uploads-picker', identityId],
    enabled: !!identityId,
    queryFn: () => listMyUploads(identityId!),
  });
  const sharedVideos = useQuery({
    queryKey: ['shared-library-picker'],
    queryFn: () => listSharedLibrary(),
  });

  async function handleAddMany(videoIds: string[]) {
    if (videoIds.length === 0) return;
    setPending(true);
    setError(null);
    setSummary(null);
    const results = await mapWithConcurrency(
      videoIds,
      async (videoId) => {
        if (isShared) {
          await editSharedPlaylist(playlist.ownerId, playlist.playlistId, { addVideoId: videoId });
        } else {
          await addVideoToPlaylist(playlist, videoId);
        }
      },
      8,
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected');
    if (succeeded > 0) onAdded();
    setSummary(
      failed.length === 0
        ? `Added ${succeeded} video${succeeded === 1 ? '' : 's'}.`
        : `Added ${succeeded} of ${videoIds.length}. ${failed.length} rejected — ${summarizeAddFailures(failed.map((f) => f.reason))}.`,
    );
    setPending(false);
  }

  const existing = new Set(playlist.videoIds);
  const myOptions = (myUploads.data ?? []).filter(
    (u) => u.status === 'ready' && !existing.has(u.uploadId),
  );
  const sharedOptions = (sharedVideos.data ?? []).filter((v) => !existing.has(v.videoId));

  const options: PickerOption[] = [
    ...myOptions.map((u) => ({ id: u.uploadId, label: u.filename, group: 'My library' })),
    ...sharedOptions.map((v) => ({ id: v.videoId, label: v.title, group: 'Shared library' })),
  ];

  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      <h2>Add videos</h2>
      <SearchablePicker
        options={options}
        multi
        onSelectMany={handleAddMany}
        placeholder="Search your library and the shared library…"
        disabled={pending}
      />
      {summary && <p className="muted">{summary}</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function summarizeAddFailures(reasons: unknown[]): string {
  const counts = new Map<string, number>();
  for (const reason of reasons) {
    const desc = playlistErrorMessage(reason, "couldn't be added");
    counts.set(desc, (counts.get(desc) ?? 0) + 1);
  }
  return [...counts.entries()].map(([desc, n]) => `${n} — ${desc}`).join('; ');
}
